/*
 * Project: bellcloud
 * File: apps/frontend/src/ui/components/common/input/DateRangePicker.tsx
 * Author: SWAN
 * Created: 2025-02-19
 * Description: 기간 선택 컴포넌트 (From ~ To)
 */

import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { AppTheme } from '@/theme/index';

export interface DateRange {
  from: string;
  to: string;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  disabled?: boolean;
  fromLabel?: string;
  toLabel?: string;
  min?: string;
  max?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  labelSize?: 'xs' | 'sm' | 'md' | 'lg';
  labelWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
  width?: string;
  labelWidth?: string;
  gap?: string;
  errorMessage?: string;
  showError?: boolean;
}

const Container = styled.div<{
  width?: string;
  gap?: string;
}>`
  display: flex;
  flex-direction: column;
  width: ${({ width }) => width || 'auto'};
  gap: ${({ gap, theme }) => gap || `${theme.spacing[2]}px`};
`;

const DateInputRow = styled.div<{
  gap?: string;
}>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${({ gap, theme }) => gap || `${theme.spacing[3]}px`};
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  position: relative;
`;

const LabelButton = styled.div<{
  $size?: 'xs' | 'sm' | 'md' | 'lg';
  $weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  $width?: string;
}>`
  width: ${({ $width }) => $width || '70px'};
  height: 26px;
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[4]}px`};
  border: ${({ theme }) => theme.borderWidth.md}px solid
    ${({ theme }) => theme.colors.border.interactive.default};
  border-right: none;
  border-top-left-radius: ${({ theme }) => theme.borderRadius.md}px;
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.surface.control.emphasis};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const InputWrapper = styled.div`
  width: 140px;
  position: relative;
`;

const StyledDateInput = styled.input<{
  disabled?: boolean;
  theme?: AppTheme;
  $size?: 'xs' | 'sm' | 'md' | 'lg';
  $hasError?: boolean;
}>`
  width: 140px;
  height: 26px;

  background: ${({ theme }) => theme.colors.surface.control.default};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: left;
  gap: ${({ theme }) => theme.spacing[2]}px;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  border: ${({ theme }) => theme.borderWidth.md}px solid
    ${({ theme }) => theme.colors.border.interactive.default};
  border-left: none;
  border-radius: 0;
  border-top-right-radius: ${({ theme }) => theme.borderRadius.md}px;
  border-bottom-right-radius: ${({ theme }) => theme.borderRadius.md}px;
  b &:focus {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.border.feedback.error : theme.colors.border.indicator};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.surface.control.disabled};
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.border.feedback.error : theme.colors.border.interactive.hover};
  }

  &::-webkit-datetime-edit {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &::-webkit-datetime-edit-fields-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
    position: absolute;
    right: 4px;
  }

  &::-webkit-calendar-picker-indicator:hover {
    opacity: 1;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.status.danger.stroke.text};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  padding-left: ${({ theme }) => theme.spacing[2]}px;
  margin-top: -${({ theme }) => theme.spacing[1]}px;
`;

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  disabled = false,
  fromLabel = 'From',
  toLabel = 'To',
  min,
  max,
  size = 'md',
  labelSize = 'sm',
  labelWeight = 'semibold',
  width,
  labelWidth,
  gap,
  errorMessage,
  showError = true,
}) => {
  const { t } = useTranslation();
  const [internalError, setInternalError] = useState<string | null>(null);

  useEffect(() => {
    if (!value.from || !value.to) {
      setInternalError(null);
      return;
    }

    const fromDate = new Date(value.from);
    const toDate = new Date(value.to);

    if (fromDate > toDate) {
      setInternalError('시작 날짜는 종료 날짜보다 이전이어야 합니다.');
    } else {
      setInternalError(null);
    }
  }, [value.from, value.to]);

  const hasError = Boolean(internalError || errorMessage);
  const displayError = errorMessage || internalError;

  const handleFromChange = (newFrom: string) => {
    onChange({ ...value, from: newFrom });
  };

  const handleToChange = (newTo: string) => {
    onChange({ ...value, to: newTo });
  };

  return (
    <Container width={width} gap={gap}>
      <DateInputRow gap={gap}>
        <DateInputGroup>
          <LabelButton $size={labelSize} $weight={labelWeight} $width={labelWidth}>
            {t(fromLabel)}
          </LabelButton>
          <InputWrapper>
            <StyledDateInput
              type="date"
              value={value.from}
              onChange={e => handleFromChange(e.target.value)}
              disabled={disabled}
              min={min}
              max={value.to || max}
              $size={size}
              $hasError={hasError}
            />
          </InputWrapper>
        </DateInputGroup>

        <DateInputGroup>
          <LabelButton $size={labelSize} $weight={labelWeight} $width={labelWidth}>
            {t(toLabel)}
          </LabelButton>
          <InputWrapper>
            <StyledDateInput
              type="date"
              value={value.to}
              onChange={e => handleToChange(e.target.value)}
              disabled={disabled}
              min={value.from || min}
              max={max}
              $size={size}
              $hasError={hasError}
            />
          </InputWrapper>
        </DateInputGroup>
      </DateInputRow>

      {showError && hasError && displayError && <ErrorMessage>{displayError}</ErrorMessage>}
    </Container>
  );
};
